import React from 'react';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { getStoredRiskPercent } from './tradeDefaults';



// Get decimal places for price formatting
const getDecimalPlaces = (instrument, price) => {
  if (price !== undefined && price !== null) {
    if (price > 1000) return 2;
    if (price > 1) return 4;
    return 5;
  }

  if (!instrument) return 5;
  const normalized = instrument.replace('_', '').toUpperCase();
  if (normalized.includes('JPY')) return 3;
  if (['BTC', 'ETH', 'SOL', 'NDX', 'SPX', 'US30'].some(s => normalized.includes(s))) return 2;
  return 5;
};

// Generate a sample trade based on current price
const generateSampleTrade = (instrument, currentPrice) => {
  const normalizedInstrument = instrument?.replace('_', '').toUpperCase() || 'EURUSD';

  // Default to a reasonable fallback price if no current price
  let entryPrice = currentPrice;
  if (!entryPrice || entryPrice <= 0) {
    // Fallback prices based on typical market values
    if (normalizedInstrument.includes('BTC')) {
      entryPrice = 95000.00;
    } else if (normalizedInstrument.includes('ETH')) {
      entryPrice = 3200.00;
    } else if (normalizedInstrument.includes('SOL')) {
      entryPrice = 150.00;
    } else if (normalizedInstrument.includes('JPY')) {
      entryPrice = 150.00;
    } else if (normalizedInstrument.startsWith('GBP')) {
      entryPrice = 1.2700;
    } else {
      entryPrice = 1.0850;
    }
  }

  const decimals = getDecimalPlaces(instrument, entryPrice);

  // Determine risk and reward distances based on asset class
  // Forex: tighter stops (~0.1-0.2%)
  // Crypto: wider stops (~2-3%)
  // Indices/Stocks: medium (~1%)

  let stopDistancePercent = 0.002; // Default 0.2%

  if (['BTC', 'ETH', 'SOL', 'DOGE', 'XRP'].some(s => normalizedInstrument.includes(s))) {
    stopDistancePercent = 0.02; // 2% for crypto
  } else if (['NDX', 'SPX', 'US30', 'NAS', 'GER'].some(s => normalizedInstrument.includes(s))) {
    stopDistancePercent = 0.01; // 1% for indices
  } else if (normalizedInstrument.includes('JPY') || normalizedInstrument.includes('XAU')) {
    stopDistancePercent = 0.005; // 0.5% for volatile pairs/gold
  }

  // Target is 2x the stop distance (2:1 Reward:Risk)
  const targetDistancePercent = stopDistancePercent * 2;

  // Long trade
  const stopPrice = parseFloat((entryPrice * (1 - stopDistancePercent)).toFixed(decimals));
  const targetPrice = parseFloat((entryPrice * (1 + targetDistancePercent)).toFixed(decimals));

  return {
    instrument: normalizedInstrument,
    direction: 'LONG',
    entryPrice: parseFloat(entryPrice.toFixed(decimals)),
    stopPrice,
    targetPrice,
    riskPercent: getStoredRiskPercent(),
  };
};

const EmptyState = ({ onCreateTrade, onLoadSampleTrade, selectedInstrument, currentPrice }) => {
  const handleLoadSample = () => {
    if (onLoadSampleTrade) {
      const normalizedInstrument = selectedInstrument?.replace('_', '').toUpperCase() || 'EURUSD';
      const sampleTrade = generateSampleTrade(normalizedInstrument, currentPrice);
      onLoadSampleTrade(sampleTrade);
    }
  };

  return (
    <div className="conceptgate-empty-state">
      <div className="empty-icon">
        <AssessmentIcon />
      </div>
      <h3>Welcome to ConceptGate</h3>
      <p className="empty-state-hint">Enter your trade details above to validate against your rules, or try a sample trade to see how it works.</p>
      <div className="empty-state-buttons">
        {onLoadSampleTrade && (
          <button
            className="conceptgate-btn conceptgate-btn-primary"
            onClick={handleLoadSample}
            title="Load a sample trade to try out the validation flow"
          >
            <AutoFixHighIcon fontSize="small" />
            Try Sample Trade
          </button>
        )}
        <button
          className="conceptgate-btn conceptgate-btn-secondary"
          onClick={onCreateTrade}
        >
          <AddIcon fontSize="small" />
          Enter Manually
        </button>
      </div>
    </div>
  );
};

export default EmptyState;
